import { Record } from 'immutable';
import { LocaleService } from '@sm360/widget-commons-core-services';

import { DATA_TYPES, DEFAULT_MODEL_TRIM_CODE, ITEM_FLAG } from 'commons-vehicle/constants/dataTypes.js';
import VEHICLE from 'commons-vehicle/constants/vehicle.js';
import { ODOMETER } from 'commons-vehicle/constants/currencyFormats.js';

const VehicleRecord = Record({
    id: '',
    vehicleId: null,
    vehicleCondition: 'new',
    newCarTrimId: 0,
    year: 0,
    make: {
        slug: '',
        name: '',
    },
    model: {
        slug: '',
        name: '',
    },
    bodyStyle: {
        slug: '',
        name: '',
    },
    bodyClassification: {
        slug: '',
        name: '',
    },
    bodyDimension: {
        slug: '',
        name: '',
    },
    driveTrain: {
        slug: '',
        name: '',
        abbreviation: '',
    },
    transmission: {
        name: '',
        speeds: '',
    },
    engine: {
        name: '',
        power: '',
        fuelConsumption: {
            city: '',
            highway: '',
        },
    },
    modelTrim: {
        slug: '',
        name: '',
        nameExtension: '',
    },
    modelTrimCode: '',
    configurationId: 0,
    configurationToken: '',
    packageCode: '',
    packageDescription: '',
    bodyColourStatus: 0,
    exteriorColourPhotos: {
        code: '',
        description: '',
        tile: '',
        side: '',
        front: '',
        rear: '',
    },
    sold: false,
    locked: false,
    stockNumber: '',
    vin: '',
    discontinued: false,
    online: true,
    showroomVehicleId: '',
});

class Vehicle extends VehicleRecord {
    isAvailable() {
        return !(this.sold || this.locked || this.discontinued || !this.online);
    }

    getIsVehicleNew() {
        return this.vehicleCondition === 'new' || this.vehicleCondition === 'newDemonstrator';
    }

    getMake() {
        return (this.make && this.make.name) || '';
    }

    getModel() {
        return (this.model && this.model.name) || '';
    }

    getYearMakeModel() {
        return this.year > 0
            ? LocaleService.t('VEHICLE_MODEL.YEAR_MAKE_MODEL', {
                  year: this.year,
                  make: this.getMake(),
                  model: this.getModel(),
              })
            : '';
    }

    getYearMakeModelTrim() {
        if (this.year === 0) {
            return '';
        }

        const modelTrim = this.modelTrim.name;

        return modelTrim
            ? LocaleService.t('VEHICLE_MODEL.YEAR_MAKE_MODEL_TRIM', {
                  year: this.year,
                  make: this.getMake(),
                  model: this.getModel(),
                  modelTrim,
              })
            : LocaleService.t('VEHICLE_MODEL.YEAR_MAKE_MODEL', {
                  year: this.year,
                  make: this.getMake(),
                  model: this.getModel(),
              });
    }

    getYearModel() {
        if (this.year === 0) {
            return '';
        }

        return LocaleService.t('VEHICLE_MODEL.YEAR_MODEL', { year: this.year, model: this.model });
    }

    getYearModelTrim() {
        if (this.year === 0) {
            return '';
        }

        const modelTrim = this.configuration !== null ? this.configuration.modelTrim.description : this.modelTrim;
        return this.modelTrim && this.modelTrim !== this.model
            ? LocaleService.t('VEHICLE_MODEL.YEAR_MODEL_TRIM', {
                  year: this.year,
                  model: this.getModel(),
                  modelTrim,
              })
            : LocaleService.t('VEHICLE_MODEL.YEAR_MODEL', {
                  year: this.year,
                  model: this.getModel(),
              });
    }

    getDiscount() {
        return this.discount > 250 ? this.discount : 0;
    }

    getOdometer() {
        if (!this.odometer) {
            return '';
        }

        const odometer = LocaleService.l(this.odometer, ODOMETER);
        return `${odometer} km`;
    }

    getFuelConsumptionAverage() {
        return Math.round(((this.fuelCity + this.fuelHighway) / 2) * 10) / 10;
    }

    getDriveTrain() {
        return (this.driveTrain && this.driveTrain.name) || '';
    }

    getEngine() {
        return (this.engine && this.engine.name) || '';
    }

    getTransmission() {
        return this.transmission ? `${this.transmission.name} ${this.transmission.speeds}` : '';
    }

    getPaymentCalculatorVehicleParameters() {
        return {
            vehicleConditionType: this.vehicleConditionType,
            year: this.year,
            make: this.make,
            model: this.model,
            bodyStyleCode: this.bodyStyleCode,
            driveTrainCode: this.driveTrainCode,
            odometer: this.odometer,
            sellingPrice: this.sellingPrice,
            vin: this.vin,
        };
    }

    getDefaultVehicleQuoteItem() {
        return {
            dataType: DATA_TYPES.FEATURE.DATA_TYPE_ID,
            typeID: DATA_TYPES.FEATURE.TYPES.MODEL_TRIM,
            code: DEFAULT_MODEL_TRIM_CODE,
            flag: ITEM_FLAG.SELECTED,
            amounts: {
                retail: this.sellingPrice,
            },
            description: {
                descriptions: [this.model],
            },
        };
    }

    getShowroomVehicle() {
        const {
            bodyColour,
            bodyStyle,
            id,
            vehicleId,
            catalogVehicleId,
            newCarTrimId, // V2.5
            vehicleConditionType,
            vin,
            year,
            make,
            model,
            modelTrim,
            modelTrimCode,
            modelTrimStyle,
            stockNumber,
            sold,
            odometer,
            configuration,
            configurationId,
            configurationToken,
        } = this;

        const isNewFromConfiguration =
            configuration !== null &&
            (vehicleConditionType === VEHICLE.CONDITIONS.NEW || vehicleConditionType === VEHICLE.CONDITIONS.DEMO);

        return {
            bodyColour,
            bodyStyle,
            id,
            vehicleId: String(vehicleId),
            catalogVehicleId: String(catalogVehicleId),
            newCarTrimId: String(newCarTrimId), // V2.5
            vehicleCondition: vehicleConditionType,
            vin,
            year,
            make,
            model,
            modelTrim,
            modelTrimCode: isNewFromConfiguration ? configuration.modelTrimCode : modelTrimCode,
            modelTrimStyle: isNewFromConfiguration ? configuration.modelTrimStyle : modelTrimStyle,
            configurationID: isNewFromConfiguration ? configuration.id : configurationId,
            configurationToken: configurationToken || '',
            odometer,
            sold,
            inventory: {
                stock: stockNumber,
            },
        };
    }
}

export default Vehicle;
